import React, { useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Upload } from "lucide-react";
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';

interface CameraAvatarUploadProps {
  userId: string;
  avatarUrl: string | null;
  setAvatarUrl: (url: string | null) => void;
  isUploading: boolean;
  onAvatarChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}

export function CameraAvatarUpload({
  userId,
  avatarUrl,
  setAvatarUrl,
  isUploading,
  onAvatarChange
}: CameraAvatarUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isNative = Capacitor.isNativePlatform();

  const handleSelectPhoto = async () => {
    if (!isNative) {
      // Fall back to file input on web
      handleFileSelect();
      return;
    }

    try {
      const image = await CapacitorCamera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt, // Let iOS show the action sheet with camera/library options
        width: 500,
        height: 500
      });

      if (image.dataUrl) {
        // Convert data URL to File object
        const response = await fetch(image.dataUrl);
        const blob = await response.blob();
        const file = new File([blob], `avatar-${Date.now()}.jpg`, { type: 'image/jpeg' });

        // Create a synthetic event to use with existing onAvatarChange handler
        const syntheticEvent = {
          target: {
            files: [file]
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        await onAvatarChange(syntheticEvent);
      }
    } catch (error) {
      console.error('Error selecting photo:', error);
      toast.error('Failed to select photo. Please try again.');
    }
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <Avatar className="h-24 w-24">
        <AvatarImage src={avatarUrl || undefined} alt={userId} />
        <AvatarFallback className="text-lg">{userId?.substring(0, 2)?.toUpperCase() || "U"}</AvatarFallback>
      </Avatar>
      
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">
          Avatar
        </Label>
        
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleSelectPhoto}
            disabled={isUploading}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            {isUploading ? (
              <div className="flex items-center">
                <Spinner className="mr-2 h-4 w-4" />
                Uploading...
              </div>
            ) : (
              "Select Photo"
            )}
          </Button>
          
          {avatarUrl && (
            <Button 
              type="button"
              variant="outline" 
              size="sm"
              onClick={() => setAvatarUrl(null)}
            >
              Remove
            </Button>
          )}
        </div>
        
        {/* Hidden file input for web fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          disabled={isUploading}
          onChange={onAvatarChange}
        />
        
        <p className="text-xs text-muted-foreground">
          Select from photo library or take a new photo. Recommended: Square image, 5MB max.
        </p>
      </div>
    </div>
  );
}
